import { useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, Link as LinkIcon } from "@phosphor-icons/react";

/**
 * A pill bar that lets an admin paste a Dry Ice Marketplace URL and
 * auto-fill the current admin CRUD form. Only shows when `enabled`.
 */
export function MarketplaceImportBar({ onImport, testIdPrefix = "marketplace-import" }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const doImport = async () => {
    if (!url.trim()) {
      toast.error("Paste a Dry Ice Marketplace product URL");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/import-from-marketplace", { url: url.trim() });
      onImport(data);
      setLastResult(data);
      toast.success(`Imported "${data.name || data.title}" from marketplace`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="md:col-span-2 border border-[#00D4FF]/30 bg-[#00D4FF]/[0.03] p-4" data-testid={testIdPrefix}>
      <div className="flex items-center gap-2 mb-3">
        <LinkIcon size={14} weight="bold" className="text-[#00D4FF]" />
        <div className="section-label">IMPORT FROM DRY ICE MARKETPLACE</div>
      </div>
      <p className="font-mono text-[11px] text-[#9CA3AF] mb-3 leading-relaxed">
        Paste a marketplace product URL (e.g. <span className="text-[#00D4FF]">https://dryicemarketplace.com/products/&lt;id&gt;</span>). We'll auto-fill everything below — you just set the member price.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://dryicemarketplace.com/products/prod-p410s"
          className="input-rugged flex-1 text-sm"
          data-testid={`${testIdPrefix}-url-input`}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doImport(); } }}
        />
        <button
          type="button"
          onClick={doImport}
          disabled={loading || !url.trim()}
          className="btn-primary text-xs whitespace-nowrap"
          data-testid={`${testIdPrefix}-btn`}
        >
          <DownloadSimple size={14} weight="bold" /> {loading ? "IMPORTING…" : "IMPORT"}
        </button>
      </div>
      {lastResult && (
        <div className="mt-3 flex flex-wrap gap-2 font-mono text-[11px] text-[#9CA3AF]" data-testid={`${testIdPrefix}-result`}>
          <span className="tag tag-active">IMPORTED</span>
          <span>SKU: <span className="text-white">{lastResult.sku || "—"}</span></span>
          <span>· MSRP: <span className="text-white">{formatMoney(lastResult.msrp_price)}</span></span>
          <span>· Images: <span className="text-white">{(lastResult.images || []).length}</span></span>
          {lastResult.youtube_url && <span>· <span className="text-[#00D4FF]">Video attached</span></span>}
        </div>
      )}
    </div>
  );
}
